import { useCallback, useEffect, useRef, useState } from "react";
import { axios } from "helper/axios";
import { parseNotificationsResponse } from "utils/parseNotifications";

const NOTIFICATIONS_API = "dashboard/notification-data";
const UPDATE_NOTIFICATION_API = "dashboard/update-notification";
const PAGE_LIMIT = 10;

export const useNotifications = ({ enabled = false } = {}) => {
  const [notifications, setNotifications] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const requestIdRef = useRef(0);
  const markedSeenIdsRef = useRef(new Set());
  const notificationsRef = useRef([]);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const markAsSeen = useCallback(async (ids) => {
    const uniqueIds = [...new Set(ids)].filter(
      (id) => id != null && !markedSeenIdsRef.current.has(id),
    );

    if (!uniqueIds.length) return;

    uniqueIds.forEach((id) => markedSeenIdsRef.current.add(id));

    setNotifications((prev) =>
      prev.map((item) =>
        uniqueIds.includes(item.id) ? { ...item, isSeen: true } : item,
      ),
    );

    try {
      await axios.post(
        UPDATE_NOTIFICATION_API,
        { id: uniqueIds, isSeen: true },
        { skipErrorToast: true },
      );
    } catch (err) {
      console.error("Failed to mark notifications as seen:", err);
      uniqueIds.forEach((id) => markedSeenIdsRef.current.delete(id));
      setNotifications((prev) =>
        prev.map((item) =>
          uniqueIds.includes(item.id) ? { ...item, isSeen: false } : item,
        ),
      );
    }
  }, []);

  const markLoadedAsSeen = useCallback(() => {
    const unseenIds = notificationsRef.current
      .filter((item) => !item.isSeen)
      .map((item) => item.id);

    if (!unseenIds.length) return Promise.resolve();
    return markAsSeen(unseenIds);
  }, [markAsSeen]);

  const fetchPage = useCallback(async (pageToLoad, { append } = {}) => {
    const requestId = ++requestIdRef.current;

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await axios.get(NOTIFICATIONS_API, {
        params: { page: pageToLoad, limit: PAGE_LIMIT },
        skipErrorToast: true,
      });

      if (requestId !== requestIdRef.current) return;

      const { notifications: parsed, total } =
        parseNotificationsResponse(response);

      setNotifications((prev) => {
        const next = append ? [...prev, ...parsed] : parsed;
        setHasMore(next.length < total && parsed.length > 0);
        return next;
      });
      setTotalCount(total);
      setPage(pageToLoad);
    } catch (err) {
      console.error("Failed to load notifications:", err);
      if (requestId !== requestIdRef.current) return;
      setError(err);
      if (!append) {
        setNotifications([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    requestIdRef.current += 1;
    markedSeenIdsRef.current = new Set();
    setNotifications([]);
    setTotalCount(0);
    setPage(1);
    setHasMore(true);
    fetchPage(1, { append: false });

    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, fetchPage]);

  const loadMore = useCallback(() => {
    if (!enabled || loading || loadingMore || !hasMore) return;
    fetchPage(page + 1, { append: true });
  }, [enabled, fetchPage, hasMore, loading, loadingMore, page]);

  const unreadCount = notifications.filter((item) => !item.isSeen).length;

  return {
    notifications,
    totalCount,
    unreadCount,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    markAsSeen,
    markLoadedAsSeen,
  };
};
